import cornerstoneTools from "cornerstone-tools";
const cursors = cornerstoneTools.import("tools/cursors");
const BaseAnnotationTool = cornerstoneTools.importInternal(
  "base/BaseAnnotationTool"
);
const external = cornerstoneTools.external;
const draw = cornerstoneTools.importInternal("drawing/draw");
const getNewContext = cornerstoneTools.importInternal("drawing/getNewContext");
const drawTextBox = cornerstoneTools.importInternal("drawing/drawTextBox");
const drawHandles = cornerstoneTools.importInternal("drawing/drawHandles");

const getToolState = cornerstoneTools.getToolState;
const textStyle = cornerstoneTools.textStyle;
const toolColors = cornerstoneTools.toolColors;
const getModule = cornerstoneTools.getModule;

const getRGBPixels = cornerstoneTools.importInternal("util/getRGBPixels");
const calculateSUV = cornerstoneTools.importInternal("util/calculateSUV");
const throttle = cornerstoneTools.importInternal("util/throttle");
const { probeCursor } = cursors;

// Utilities
//import getRGBPixels from '../../util/getRGBPixels.js';
//import calculateSUV from '../../util/calculateSUV.js';
//import { probeCursor } from '../cursors/index.js';
//import throttle from '../../util/throttle';
//import { getModule } from '../../store/index';

/**
 * @public
 * @class ProbeTool
 * @memberof Tools.Annotation
 * @classdesc Tool which provides a probe of the image data at the
 * desired position.
 * @extends Tools.Base.BaseAnnotationTool
 */
export default class ProbeTool extends BaseAnnotationTool {
  constructor(props = {}) {
    const defaultProps = {
      name: "CorneaProbe",
      supportedInteractionTypes: ["Mouse", "Touch"],
      svgCursor: probeCursor,
      configuration: {
        drawHandles: true,
        renderDashed: false,
        probeType: "cornea",
        color: "#FF0000", // red
      },
    };

    super(props, defaultProps);

    this.throttledUpdateCachedStats = throttle(this.updateCachedStats, 110);
  }

  createNewMeasurement(eventData) {
    const goodEventData =
      eventData && eventData.currentPoints && eventData.currentPoints.image;
    const processButtons = document.getElementsByClassName("processButton");
    const processButton = processButtons[0]; // Get the first element with class "processButton"

    if (!goodEventData) {
      return;
    } else {
      if (
        processButton &&
        processButton.classList &&
        !processButton.classList.contains("bg-red-600")
      ) {
        processButton.classList.remove("bg-purple-800");
        processButton.classList.add("bg-red-600");
      }
    }

    return {
      visible: true,
      active: true,
      color: this.configuration.color || undefined,
      invalidated: true,
      handles: {
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    };
  }

  /**
   *
   *
   * @param {*} element
   * @param {*} data
   * @param {*} coords
   * @returns {Boolean}
   */
  pointNearTool(element, data, coords) {
    const hasEndHandle = data && data.handles && data.handles.end;
    const validParameters = hasEndHandle;

    if (!validParameters || data.visible === false) {
      return false;
    }

    const probeCoords = external.cornerstone.pixelToCanvas(
      element,
      data.handles.end
    );
    return external.cornerstoneMath.point.distance(probeCoords, coords) < 5;
  }

  updateCachedStats(image, element, data) {
    const x = Math.round(data.handles.end.x);
    const y = Math.round(data.handles.end.y);

    const stats = {};

    if (x >= 0 && y >= 0 && x < image.columns && y < image.rows) {
      stats.x = x;
      stats.y = y;

      if (image.color) {
        stats.storedPixels = getRGBPixels(element, x, y, 1, 1);
      } else {
        stats.storedPixels = external.cornerstone.getStoredPixels(
          element,
          x,
          y,
          1,
          1
        );
        stats.sp = stats.storedPixels[0];
        stats.mo = stats.sp * image.slope + image.intercept;
        stats.suv = calculateSUV(image, stats.sp);
      }
    }

    data.cachedStats = stats;
    data.invalidated = false;
  }

  renderToolData(evt) {
    const eventData = evt.detail;
    const { renderDashed } = this.configuration;
    const toolData = getToolState(evt.currentTarget, this.name);
    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = getNewContext(eventData.canvasContext.canvas);
    const { image, element } = eventData;
    const fontHeight = textStyle.getFontSize();
    const lineDash = getModule("globalConfiguration").configuration.lineDash;

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }

      draw(context, (context) => {
        // const color = toolColors.getColorIfActive(data);
        const color = "#FF0000";
        if (this.configuration.drawHandles) {
          // Draw the handles
          let handleOptions = { handleRadius: 2, color };
          if (renderDashed) {
            handleOptions.lineDash = lineDash;
          }
          drawHandles(context, eventData, data.handles, handleOptions);
        }
        // Update textbox stats
        if (data.invalidated === true) {
          if (data.cachedStats) {
            this.throttledUpdateCachedStats(image, element, data);
          } else {
            this.updateCachedStats(image, element, data);
          }
        }

        const { x, y, storedPixels, sp, mo, suv } = data.cachedStats;

        if (x >= 0 && y >= 0 && x < image.columns && y < image.rows) {
          let text;

          if (image.color) {
            // Construct string with RGB values
            // To show RGB just uncomment the line str variable and remove the one empty
            let str = ``;
            // let str = `R: ${storedPixels[0]} G: ${storedPixels[1]} B: ${storedPixels[2]}`;

            // Check if there's existing text to append
            if (data.label !== undefined) {
              str += ` - ${data.label}`;
            }

            if (data.calibration !== undefined) {
              str += ` - ${data.calibration}`;
            }

            // Coords for text
            const coords = {
              // Translate the x/y away from the cursor
              x: data.handles.end.x + 3,
              y: data.handles.end.y - 3,
            };
            const textCoords = external.cornerstone.pixelToCanvas(
              eventData.element,
              coords
            );

            drawTextBox(
              context,
              str,
              textCoords.x,
              textCoords.y + fontHeight + 5,
              color
            );

            // Set text to include x, y coordinates
            text = `${x}, ${y}`;
          } else {
            // Draw text
            let str = `SP: ${sp} MO: ${parseFloat(mo.toFixed(3))}`;
            if (suv) {
              str += ` SUV: ${parseFloat(suv.toFixed(3))}`;
            }

            // Coords for text
            const coords = {
              // Translate the x/y away from the cursor
              x: data.handles.end.x + 3,
              y: data.handles.end.y - 3,
            };
            const textCoords = external.cornerstone.pixelToCanvas(
              eventData.element,
              coords
            );

            drawTextBox(
              context,
              str,
              textCoords.x,
              textCoords.y + fontHeight + 5,
              color
            );

            // Set text to include x, y coordinates
            text = `${x}, ${y}`;
          }

          // Coords for text
          const coords = {
            // Translate the x/y away from the cursor
            x: data.handles.end.x + 3,
            y: data.handles.end.y - 3,
          };
          const textCoords = external.cornerstone.pixelToCanvas(
            eventData.element,
            coords
          );

          // Draw text box with x, y coordinates
          // drawTextBox(context, text, textCoords.x, textCoords.y, color);
        }
      });
    }
  }
}
